import { useTruncationDetection } from '@anchorage/hooks';
import { Badge } from '@latitude/badge';
import { Button } from '@latitude/button';
import { ProgressBar } from '@latitude/progress-bar';
import { Separator } from '@latitude/separator';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { Tooltip, TooltipProvider } from '@latitude/tooltip';
import type { ReactNode, Ref } from 'react';
import type {
  HeaderProps,
  HeaderSlotWrapper,
  TrailingActionProps,
} from 'types';

import {
  headerVariants,
  titleBlockVariants,
  titleRowVariants,
} from './variants';

function TrailingActionButton({
  tooltip,
  ...buttonProps
}: TrailingActionProps) {
  const button = <Button size="small" {...buttonProps} />;
  // Radix throws when a tooltip has no provider above it, so the header carries
  // its own rather than making every caller supply one. Nesting inside an
  // existing provider is harmless: the inner one just wins on timing.
  return tooltip ? (
    <TooltipProvider>
      <Tooltip text={tooltip}>{button}</Tooltip>
    </TooltipProvider>
  ) : (
    button
  );
}

const wrapTitle = (wrapper: HeaderSlotWrapper | undefined, node: ReactNode) =>
  wrapper ? wrapper(node) : node;

type TitleProps = {
  title: ReactNode;
  titleVariant: HeaderProps['titleVariant'];
  wrapper: HeaderSlotWrapper | undefined;
  testID: string;
};

// Only mounted when truncation is actually requested, so
// useTruncationDetection is not a hard dependency for every Header render.
const TruncatableTitle = ({
  title,
  titleVariant,
  wrapper,
  testID,
}: TitleProps & { title: string }) => {
  const { ref, isTruncated } = useTruncationDetection();

  return (
    <TooltipProvider>
      <Tooltip
        text={title}
        wrapperClassName="min-w-0"
        hideTooltip={!isTruncated}
      >
        {wrapTitle(
          wrapper,
          <Text
            ref={ref}
            variant={titleVariant}
            color="strong"
            className="block truncate"
            data-testid={testID}
          >
            {title}
          </Text>,
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

const HeaderTitle = ({ title, titleVariant, wrapper, testID }: TitleProps) =>
  wrapTitle(
    wrapper,
    <Text
      variant={titleVariant}
      color="strong"
      className="min-w-0"
      data-testid={testID}
    >
      {title}
    </Text>,
  );

/**
 * The shared header for Latitude surfaces: modals, side panels, cards, and
 * sections. Renders an optional leading element, a title block (title, badges,
 * optional subtext), a right-aligned actions area, a bottom separator, and an
 * optional progress bar overlaid on the bottom edge. A progress bar replaces
 * the separator, since both draw on the bottom edge.
 *
 * 56px tall by default, 84px once a subtext is shown.
 */
const Header = ({
  title,
  titleVariant = 'headingSmall',
  leadingElement,
  badge,
  badges,
  subtext,
  actions,
  trailingActions,
  truncateTitle = false,
  separator = true,
  progress,
  titleWrapper,
  className,
  'data-testid': testID = 'header',
  ref,
}: HeaderProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const buttonActions = trailingActions ?? [];
  const badgeContent =
    badges ?? (badge && <Badge variant="neutral" size="tiny" {...badge} />);
  // Plain truthiness, not `!= null`: `actions={isAdmin && <Button />}` passes
  // `false` when the guard fails, and that has to read as "no actions" rather
  // than reserve an empty slot in the row's gap.
  const renderedActions =
    actions || (buttonActions.length > 0 ? buttonActions : null);
  // The progress bar owns the bottom edge, and the separator draws on the same
  // 1px the bar sits on. A progress bar is its own boundary, so it wins.
  const showSeparator = separator && !progress;
  const titleProps = {
    titleVariant,
    wrapper: titleWrapper,
    testID: `${testID}-title`,
  };

  return (
    <div
      ref={ref}
      data-testid={testID}
      className={cn(headerVariants(), className)}
    >
      {leadingElement && (
        <div className="shrink-0" data-testid={`${testID}-leading-element`}>
          {leadingElement}
        </div>
      )}

      <div className={titleBlockVariants()}>
        <div className={titleRowVariants()}>
          {truncateTitle && typeof title === 'string' && title ? (
            <TruncatableTitle title={title} {...titleProps} />
          ) : (
            <HeaderTitle title={title} {...titleProps} />
          )}

          {badgeContent && (
            <div className="shrink-0" data-testid={`${testID}-badge`}>
              {badgeContent}
            </div>
          )}
        </div>

        {subtext != null && (
          <div className="flex items-center" data-testid={`${testID}-subtext`}>
            {typeof subtext === 'string' ? (
              <Text variant="bodyRegular" color="muted" className="break-words">
                {subtext}
              </Text>
            ) : (
              subtext
            )}
          </div>
        )}
      </div>

      {renderedActions && (
        <div
          className="gap-spacing-sys-spacing-8 flex shrink-0 items-center"
          data-testid={`${testID}-trailing-action`}
        >
          {actions ||
            buttonActions.map((action, index) => (
              <TrailingActionButton
                key={action['data-testid'] || index}
                {...action}
              />
            ))}
        </div>
      )}

      {progress && (
        <div
          className="absolute inset-x-0 bottom-0"
          data-testid={`${testID}-progress`}
        >
          <ProgressBar size="small" {...progress} />
        </div>
      )}

      {showSeparator && (
        <Separator
          orientation="horizontal"
          className="absolute bottom-0 left-0 right-0"
          data-testid={`${testID}-separator`}
        />
      )}
    </div>
  );
};

Header.displayName = 'Header';

export {
  Header,
  type HeaderProps,
  type HeaderSlotWrapper,
  type TrailingActionProps,
};
